import { useEffect, useState, createContext, useContext, useCallback, lazy, Suspense } from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Eagerly loaded (critical path)
import LandingPage from "@/pages/LandingPage";
import AuthCallback from "@/pages/AuthCallback";
import PublicSetPage from "@/pages/PublicSetPage";

// Lazy loaded pages (code-split)
const StudentDashboard = lazy(() => import("@/pages/StudentDashboard"));
const ParentDashboard = lazy(() => import("@/pages/ParentDashboard"));
const SchoolDashboard = lazy(() => import("@/pages/SchoolDashboard"));
const JoinPage = lazy(() => import("@/pages/JoinPage"));
const QuickstartPage = lazy(() => import("@/pages/QuickstartPage"));
const TutorChat = lazy(() => import("@/pages/TutorChat"));
const VoiceTutor = lazy(() => import("@/pages/VoiceTutor"));
const Masterclass = lazy(() => import("@/pages/Masterclass"));
const ExamMode = lazy(() => import("@/pages/ExamMode"));
const LessonPlanner = lazy(() => import("@/pages/LessonPlanner"));
const StudyPath = lazy(() => import("@/pages/StudyPath"));
const Progress = lazy(() => import("@/pages/Progress"));
const Settings = lazy(() => import("@/pages/Settings"));
const NotesSummarizer = lazy(() => import("@/pages/NotesSummarizer"));
const QuizGenerator = lazy(() => import("@/pages/QuizGenerator"));
const LessonEngine = lazy(() => import("@/pages/LessonEngine"));
const FlashcardGenerator = lazy(() => import("@/pages/FlashcardGenerator"));
const MaterialRecompositor = lazy(() => import("@/pages/MaterialRecompositor"));
const CaseLibrary = lazy(() => import("@/pages/CaseLibrary"));
const ProblemSolver = lazy(() => import("@/pages/ProblemSolver"));
const VisualSimulator = lazy(() => import("@/pages/VisualSimulator"));
const ErrorDiagnosis = lazy(() => import("@/pages/ErrorDiagnosis"));
const HomeworkScanner = lazy(() => import("@/pages/HomeworkScanner"));
const KnowledgeBase = lazy(() => import("@/pages/KnowledgeBase"));
const PeerChallenge = lazy(() => import("@/pages/PeerChallenge"));
const ParentChildDetail = lazy(() => import("@/pages/ParentChildDetail"));
const SpeechPractice = lazy(() => import("@/pages/SpeechPractice"));
const PronunciationProgress = lazy(() => import("@/pages/PronunciationProgress"));
const AccentTraining = lazy(() => import("@/pages/AccentTraining"));
const ConversationPartner = lazy(() => import("@/pages/ConversationPartner"));
const OfflineLearning = lazy(() => import("@/pages/OfflineLearning"));
const CurriculumExplorer = lazy(() => import("@/pages/CurriculumExplorer"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const PaymentCallback = lazy(() => import("@/pages/PaymentCallback"));
const Achievements = lazy(() => import("@/pages/Achievements"));
const OnboardingPage = lazy(() => import("@/pages/OnboardingPage"));
const SafetySettings = lazy(() => import("@/pages/SafetySettings"));
const MonitoringDashboard = lazy(() => import("@/pages/MonitoringDashboard"));
const WellbeingPage = lazy(() => import("@/pages/WellbeingPage"));
const MaterialsPage = lazy(() => import("@/pages/MaterialsPage"));
const MaterialDetailPage = lazy(() => import("@/pages/MaterialDetailPage"));
const StudyPlanner = lazy(() => import("@/pages/StudyPlanner"));
import { AgeGroupProvider } from "@/contexts/AgeGroupContext";
import { CelebrationProvider } from "@/components/CelebrationProvider";
import { ScreenTimeProvider } from "@/components/ScreenTimeProvider";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import LanguageDetectionNotifier from "@/components/LanguageDetectionNotifier";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// iOS Safari fallback: attach session token header when cookies may be blocked
// Uses sessionStorage (cleared on tab close) instead of localStorage for security
axios.interceptors.request.use((config) => {
  const fallbackToken = sessionStorage.getItem("_st_fb");
  if (fallbackToken && !config.headers["X-Session-Token"]) {
    config.headers["X-Session-Token"] = atob(fallbackToken);
  }
  return config;
});

// Missed-upgrade capture: when any authenticated request hits a 403
// feature_not_available, surface a conversion-ready toast with a
// one-click "Start free trial" CTA. Debounced by feature so a page that
// fires 3 gated calls doesn't show 3 toasts.
const _recentUpgradeToasts = new Set();
axios.interceptors.response.use(
  (r) => r,
  (error) => {
    try {
      const res = error.response;
      const detail = res?.data?.detail;
      if (
        res?.status === 403 &&
        detail &&
        typeof detail === "object" &&
        detail.error === "feature_not_available"
      ) {
        const key = detail.feature || "unknown";
        if (!_recentUpgradeToasts.has(key)) {
          _recentUpgradeToasts.add(key);
          setTimeout(() => _recentUpgradeToasts.delete(key), 8000);
          const featureName = detail.feature_name || "this feature";
          toast(`${featureName} is a Pro feature`, {
            description: "Try it free for 7 days — no card required.",
            duration: 9000,
            action: {
              label: "Start free trial",
              onClick: async () => {
                try {
                  await axios.post(
                    `${API}/subscription/trial/activate`,
                    {},
                    { withCredentials: true },
                  );
                  toast.success("Pro trial activated! Refreshing…");
                  setTimeout(() => window.location.reload(), 900);
                } catch (e) {
                  const msg =
                    e?.response?.data?.detail?.message ||
                    "Couldn't start trial. Please visit /pricing.";
                  toast.error(msg);
                }
              },
            },
          });
        }
      }
    } catch {
      /* never let the interceptor swallow the original error */
    }
    return Promise.reject(error);
  },
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // Try with cookies first
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch {
      // If cookies fail, the axios interceptor will have sent X-Session-Token header
      // Try again without credentials flag (avoids CORS credential conflict)
      const fallback = sessionStorage.getItem("_st_fb");
      if (fallback) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { "X-Session-Token": atob(fallback) },
          });
          setUser(response.data);
        } catch {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
    }
    localStorage.removeItem("session_token_fallback");
    sessionStorage.removeItem("_st_fb");
    setUser(null);
  };

  const updateRole = async (role) => {
    try {
      await axios.put(`${API}/auth/role`, { role }, { withCredentials: true });
      setUser(prev => ({ ...prev, role }));
    } catch (error) {
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, updateRole, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const fallback = user.role === "parent" ? "/parent"
      : (user.role === "school_admin" || user.role === "teacher") ? "/school"
      : "/dashboard";
    return <Navigate to={fallback} replace />;
  }

  return children;
};

// Route loading fallback
const RouteLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-3 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
      <p className="text-sm text-slate-500">Loading...</p>
    </div>
  </div>
);

// Route configuration — data-driven to reduce component size
const STUDENT_ROUTES = [
  { path: "/dashboard", Component: StudentDashboard },
  { path: "/tutor", Component: TutorChat },
  { path: "/voice-tutor", Component: VoiceTutor },
  { path: "/masterclass", Component: Masterclass },
  { path: "/exam-mode", Component: ExamMode },
  { path: "/study-path", Component: StudyPath },
  { path: "/notes", Component: NotesSummarizer },
  { path: "/quiz", Component: QuizGenerator },
  { path: "/lessons", Component: LessonEngine },
  { path: "/flashcards", Component: FlashcardGenerator },
  { path: "/recompose", Component: MaterialRecompositor },
  { path: "/cases", Component: CaseLibrary },
  { path: "/solver", Component: ProblemSolver },
  { path: "/simulator", Component: VisualSimulator },
  { path: "/diagnosis", Component: ErrorDiagnosis },
  { path: "/homework", Component: HomeworkScanner },
  { path: "/knowledge-base", Component: KnowledgeBase },
  { path: "/challenges", Component: PeerChallenge },
  { path: "/speech", Component: SpeechPractice },
  { path: "/pronunciation-progress", Component: PronunciationProgress },
  { path: "/accent", Component: AccentTraining },
  { path: "/conversation", Component: ConversationPartner },
  { path: "/offline", Component: OfflineLearning },
  { path: "/curriculum", Component: CurriculumExplorer },
  { path: "/wellbeing", Component: WellbeingPage },
  { path: "/materials", Component: MaterialsPage },
  { path: "/materials/:materialId", Component: MaterialDetailPage },
  { path: "/planner", Component: StudyPlanner },
];

const PARENT_ROUTES = [
  { path: "/parent", Component: ParentDashboard },
  { path: "/parent/child/:childId", Component: ParentChildDetail },
  { path: "/parent/child/:childId/safety", Component: SafetySettings },
];

const SHARED_ROUTES = [
  { path: "/progress", Component: Progress, roles: ["student", "parent"] },
  { path: "/settings", Component: Settings },
  { path: "/pricing", Component: PricingPage },
  { path: "/checkout", Component: CheckoutPage },
  { path: "/achievements", Component: Achievements },
  { path: "/onboarding", Component: OnboardingPage },
  { path: "/monitoring", Component: MonitoringDashboard },
  { path: "/lesson-planner", Component: LessonPlanner },
  { path: "/school", Component: SchoolDashboard, roles: ["school_admin", "teacher"] },
];

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<RouteLoader />}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/sets/:setId" element={<PublicSetPage />} />
      <Route path="/join/:token" element={<JoinPage />} />
      <Route path="/quickstart" element={<QuickstartPage />} />
      {STUDENT_ROUTES.map(({ path, Component }) => (
        <Route key={path} path={path} element={
          <ProtectedRoute allowedRoles={["student"]}><Component /></ProtectedRoute>
        } />
      ))}
      {PARENT_ROUTES.map(({ path, Component }) => (
        <Route key={path} path={path} element={
          <ProtectedRoute allowedRoles={["parent"]}><Component /></ProtectedRoute>
        } />
      ))}
      {SHARED_ROUTES.map(({ path, Component, roles }) => (
        <Route key={path} path={path} element={
          <ProtectedRoute allowedRoles={roles}><Component /></ProtectedRoute>
        } />
      ))}
      <Route path="/payment/callback" element={<PaymentCallback />} />
      <Route path="/payment/success" element={<PaymentCallback />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AgeGroupProvider>
            <SubscriptionProvider>
              <CelebrationProvider>
                <ScreenTimeProvider>
                  <Toaster position="top-right" richColors />
                  <LanguageDetectionNotifier />
                  <AppRouter />
                </ScreenTimeProvider>
              </CelebrationProvider>
            </SubscriptionProvider>
          </AgeGroupProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
export { API, BACKEND_URL };
